<template>
  <ResultsLoader v-if="isLoading" :class="{ hidden: landingMode }" />
  <BoxedText
    v-else-if="error || !results"
    :title="$t('errors.something_went_wrong')"
    :subtitle="$t('errors.try_later')"
  />
  <NoResults
    v-else-if="!results.providerQuotes.length"
    class="lg:max-w-4xl mx-auto"
    :amount-usd="results.corridor.amountUSD"
    :landing-mode="landingMode"
    :comparison-id="results.comparisonId"
  />
  <div v-else>
    <AppPromptMinimal v-if="!IS_MOBILE_APP && !landingMode" />
    <ComparisonResults
      :results="results"
      :loading-time="loadingTime"
      :landing-mode="landingMode"
      :historical-data="historicalData"
    />
  </div>
</template>

<script lang="ts" setup>
import type { MoneyTransfer, Organization, WithContext } from 'schema-dts'
import type {
  GQLTransferComparison,
  GQLTransferComparisonVariables,
} from './generated/transfer-comparison'
import MUTATION from './transfer-comparison.graphql'
import type { Corridor } from '@core/types/imt'
import type { HistoricalData } from './types'
import ResultsLoader from './results-loader.vue'
import NoResults from './no-results/no-results.vue'
import BoxedText from './boxed-text.vue'
import ComparisonResults from './comparison-results.vue'
import { EIGHT_WEEKS_IN_SECONDS, ONE_WEEK_IN_SECONDS } from '@core/constants/time'

const IS_MOBILE_APP = __IS_MOBILE_APP__

const props = defineProps<{
  corridor: Corridor
  metadata: { title: string; description: string }
  landingMode: boolean
  historicalData?: HistoricalData
}>()

provide('corridor', props.corridor)

const { $lang, $apollo } = useNuxtApp()
const logger = useLogger('transfer-comparison')

const url = useAbsoluteUrl()

const isLoading = ref(true)
const loadingTime = reactive({ start: Date.now(), end: -1 })

const {
  data: results,
  error,
  status,
} = await useAsyncData(
  'comparison-data',
  async () => {
    if (__IS_MOBILE_APP__) {
      const results = await getLastComparisonResults<GQLTransferComparison['results']>(
        props.corridor,
      )
      if (results) {
        return results
      }
    }

    const { data, errors } = await $apollo.mutate<
      GQLTransferComparison,
      GQLTransferComparisonVariables
    >({
      mutation: MUTATION,
      variables: {
        lang: $lang,
        from: props.corridor.countryFrom.iso,
        to: props.corridor.countryTo.iso,
        currencyFrom: props.corridor.currencyFrom.iso,
        currencyTo: props.corridor.currencyTo.iso,
        amount: Math.round(props.corridor.amount || props.corridor.defaultAmount),
        maxAge: props.landingMode ? EIGHT_WEEKS_IN_SECONDS : 0,
      },
      context: {
        cache: props.landingMode
          ? {
              type: 'revalidate',
              maxAge: ONE_WEEK_IN_SECONDS,
              revalidate: EIGHT_WEEKS_IN_SECONDS,
            }
          : { type: 'none' },
      },
    })
    if (errors) {
      throw Error(errors[0].message)
    }
    return data?.results ?? null
  },
  // On landing mode we fetch comparison during SSR
  { server: props.landingMode, lazy: __IS_MOBILE_APP__ },
)

watch(
  error,
  () => {
    if (error.value) {
      logger.error({ error: error.value }, 'Failed to load transfer comparison')
    }
  },
  { immediate: true },
)
watch(
  status,
  () => {
    if (status.value !== 'pending') {
      isLoading.value = false
      loadingTime.end = Date.now()
    }
  },
  { immediate: props.landingMode },
)

if (props.landingMode && results.value) {
  const structuredData: WithContext<MoneyTransfer> = {
    '@context': 'https://schema.org',
    '@type': 'MoneyTransfer',
    name: props.metadata.title,
    description: props.metadata.description,
    url,
    amount: {
      '@type': 'MonetaryAmount',
      currency: props.corridor.currencyFrom.iso,
      value: props.corridor.amount || props.corridor.defaultAmount,
    },
    fromLocation: { '@type': 'Country', name: props.corridor.countryFrom.labels.label },
    toLocation: { '@type': 'Country', name: props.corridor.countryTo.labels.label },
    agent: results.value.providerQuotes.map(
      ({ psp, quotes }): Organization => ({
        '@type': 'Organization',
        name: psp.displayName,
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          itemListElement: [
            {
              '@type': 'FinancialProduct',
              description: `Money Transfer ${props.corridor.currencyFrom.iso}-${props.corridor.currencyTo.iso}`,
              feesAndCommissionsSpecification: `${quotes[0].fee.total.toFixed(2)} ${
                props.corridor.currencyFrom.iso
              }`,
            },
            {
              '@type': 'ExchangeRateSpecification',
              currency: props.corridor.currencyTo.iso,
              currentExchangeRate: {
                '@type': 'UnitPriceSpecification',
                price: quotes[0].rate.toFixed(6),
                priceCurrency: props.corridor.currencyFrom.iso,
              },
            },
          ],
        },
      }),
    ),
  }
  useHead({
    script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(structuredData) }],
  })
}
</script>
